/**
 * The storyboard a lesson plays when it has no video attached.
 *
 * This is the approved mockup's own teaching format, not a fallback invented
 * to fill a hole: a short caption, a large statement, a line of body copy,
 * advanced scene by scene. It means the whole curriculum can go live before a
 * single video is filmed, and each lesson quietly upgrades itself to the real
 * player the moment its YouTube link is attached.
 *
 * The panel is deliberately pinned dark in both themes. It stands in for a
 * video frame, and a video frame does not turn white on the light theme — so
 * the colours here are literal rather than tokenised, the same call made for
 * the "Continue learning" hero.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";

import { cn } from "@/lib/utils";
import type { AcademyStoryboardScene } from "@/lib/academy";

/** How long each scene holds before advancing. Matches the mockup's pacing. */
const SCENE_MS = 5500;

export interface StoryboardPlayerProps {
  scenes: AcademyStoryboardScene[];
  /** Fired once the reader reaches the final scene — the storyboard's
   *  equivalent of watching a video to the end. */
  onFinished?: () => void;
}

export default function StoryboardPlayer({ scenes, onFinished }: StoryboardPlayerProps) {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const total = scenes.length;

  // Held in a ref so the auto-advance effect depends only on playback state —
  // a changing callback identity must not restart the timer mid-scene.
  const onFinishedRef = useRef(onFinished);
  useEffect(() => {
    onFinishedRef.current = onFinished;
  }, [onFinished]);

  // Reaching the last scene counts as having been through the lesson, however
  // the reader got there — autoplay, Next, or the scene dots.
  useEffect(() => {
    if (total > 0 && index === total - 1) onFinishedRef.current?.();
  }, [index, total]);

  useEffect(() => {
    if (!playing) return;
    const t = window.setInterval(() => {
      setIndex((i) => {
        if (i >= total - 1) {
          // Stop at the end rather than looping: the reader should be left on
          // the closing "your next move" scene, next to the action box.
          setPlaying(false);
          return i;
        }
        return i + 1;
      });
    }, SCENE_MS);
    return () => window.clearInterval(t);
  }, [playing, total]);

  const atEnd = index >= total - 1;

  const toggle = useCallback(() => {
    // Pressing play on the final scene restarts, which is the only sensible
    // reading of "play" there.
    if (!playing && atEnd) setIndex(0);
    setPlaying((p) => !p);
  }, [playing, atEnd]);

  if (total === 0) return null;
  const scene = scenes[index];

  return (
    <div
      className="relative w-full overflow-hidden rounded-xl bg-[#261f17] text-white"
      style={{ aspectRatio: "16 / 9" }}
    >
      <div className="absolute inset-x-0 top-0 flex items-start justify-between gap-3 px-6 pt-5">
        <span className="rounded bg-[#fbf8f1] px-2 py-1 text-[0.58rem] font-bold uppercase tracking-[0.14em] text-[#261f17]">
          BL Academy
        </span>
        <span className="text-[0.55rem] uppercase tracking-[0.12em] text-[#bfad8a]">
          Storyboard preview · no audio
        </span>
      </div>

      {/* The scene. Keyed so each one re-mounts and re-runs the fade, which is
          what makes the sequence feel like playback rather than text swapping. */}
      <div
        key={index}
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 px-6 sm:px-8"
        style={{ animation: "academySceneIn 420ms ease-out both" }}
      >
        {scene.label && (
          <small className="text-[0.62rem] font-semibold uppercase tracking-[0.14em] text-[#c5a768]">
            {scene.label}
          </small>
        )}
        <h3 className="heading-bold mt-2 font-display text-[1.6rem] leading-tight text-[#f1d89f] sm:text-[1.875rem]">
          {scene.headline}
        </h3>
        {scene.body && (
          <p className="mt-2.5 max-w-lg text-xs leading-6 text-[#d0c7b7] sm:text-[0.82rem]">
            {scene.body}
          </p>
        )}
      </div>

      {/* Transport */}
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-[#18130e] px-4 py-2.5">
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause storyboard" : atEnd ? "Replay storyboard" : "Play storyboard"}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#e9cf95]/40 text-[#e9cf95] transition-colors hover:bg-[#e9cf95]/15"
        >
          {playing ? (
            <Pause className="h-3 w-3 fill-current" />
          ) : atEnd ? (
            <RotateCcw className="h-3 w-3" />
          ) : (
            <Play className="ml-0.5 h-3 w-3 fill-current" />
          )}
        </button>

        <span className="shrink-0 text-[0.62rem] tabular-nums text-[#bbae97]">
          Scene {index + 1} / {total}
        </span>

        {/* Clickable scrubber — each segment is one scene. */}
        <div className="flex flex-1 gap-1">
          {scenes.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to scene ${i + 1}`}
              aria-current={i === index}
              onClick={() => {
                setPlaying(false);
                setIndex(i);
              }}
              className="group h-3 flex-1"
            >
              <span
                className={cn(
                  "block h-[3px] w-full rounded-full transition-colors",
                  i <= index ? "bg-[#d1ac5f]" : "bg-[#5c5140] group-hover:bg-[#8a7a5e]",
                )}
              />
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => {
            setPlaying(false);
            setIndex((i) => (i >= total - 1 ? 0 : i + 1));
          }}
          className="shrink-0 text-[0.68rem] font-semibold text-[#e9cf95] transition-opacity hover:opacity-80"
        >
          {atEnd ? "Replay ↺" : "Next scene →"}
        </button>
      </div>
    </div>
  );
}
